import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProduct, createProduct, updateProduct, deleteProduct } from '../../services/adminService';
import AdminProductForm from '../../components/AdminProductForm';

const AdminProductDetail = () => {
  const { id } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();

  const isNew = id === 'new';

  const [product, setProduct]       = useState(null);
  const [loading, setLoading]       = useState(!isNew);
  const [fetchError, setFetchError] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleting, setDeleting]     = useState(false);

  // Keep a ref to the latest session so token refreshes don't trigger a re-fetch
  // and wipe unsaved form edits. The fetch only needs to rerun when `id` changes.
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });

  useEffect(() => {
    if (isNew) return;

    const token = sessionRef.current?.access_token;
    if (!token) {
      setLoading(false);
      setFetchError('No hay sesión activa.');
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchData = async () => {
      try {
        const data = await getProduct(token, id);
        if (!cancelled) setProduct(data.product);
      } catch (err) {
        if (!cancelled) setFetchError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => { cancelled = true; };
  }, [id, isNew]); // intentionally excludes session — token refreshes must not reset form

  const handleSubmit = async (formData) => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      if (isNew) {
        await createProduct(session.access_token, formData);
        navigate('/admin/products', { state: { created: true } });
      } else {
        const data = await updateProduct(session.access_token, id, formData);
        setProduct(data.product);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setSaveError(err.message || 'No se pudo guardar el producto.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar "${product?.name}"? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await deleteProduct(session.access_token, id);
      navigate('/admin/products');
    } catch (err) {
      alert(`Error al eliminar: ${err.message}`);
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Cargando producto...</div>;
  }

  if (fetchError) {
    return (
      <div className="container" style={{ padding: '4rem 0' }}>
        <Link to="/admin/products" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Productos</Link>
        <p style={{ color: '#dc3545', marginTop: '1rem' }}>{fetchError}</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <Link to="/admin/products" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>
        ← Productos
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', margin: '0.75rem 0 2rem' }}>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.5rem', color: '#333', margin: 0 }}>
          {isNew ? 'Nuevo producto' : product?.name || 'Editar producto'}
        </h1>
        {!isNew && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{
              padding: '0.5rem 1.1rem',
              background: 'none',
              border: '1.5px solid #dc3545',
              borderRadius: '8px',
              color: deleting ? '#ccc' : '#dc3545',
              fontWeight: '600',
              fontSize: '0.88rem',
              cursor: deleting ? 'default' : 'pointer',
            }}
          >
            {deleting ? 'Eliminando...' : 'Eliminar producto'}
          </button>
        )}
      </div>

      {saveSuccess && (
        <div style={{
          background: '#eaf7ed',
          color: '#28a745',
          border: '1px solid #c3e6cb',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          fontWeight: '500',
        }}>
          Producto guardado correctamente.
        </div>
      )}

      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '2rem',
      }}>
        <AdminProductForm
          initialValues={isNew ? null : product}
          onSubmit={handleSubmit}
          loading={saving}
          error={saveError}
        />
      </div>
    </div>
  );
};

export default AdminProductDetail;
